
import type React from "react"
import { useState, useEffect, useRef } from "react"

interface CropOverlayProps {
  imageUrl: string
  onCropChange: (cropData: { x: number; y: number; width: number; height: number }) => void
  aspectRatio?: number
  initialCrop?: { x: number; y: number; width: number; height: number }
}

const CropOverlay: React.FC<CropOverlayProps> = ({ imageUrl, onCropChange, aspectRatio, initialCrop }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState(initialCrop || { x: 10, y: 10, width: 200, height: 200 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [resizing, setResizing] = useState(false)
  const [resizeCorner, setResizeCorner] = useState<"tl" | "tr" | "bl" | "br" | null>(null)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })

  // Initialize crop area when image loads
  useEffect(() => {
    const img = imageRef.current
    if (img) {
      const handleImageLoad = () => {
        const width = Math.min(img.width, 300)
        const height = aspectRatio ? width / aspectRatio : width

        setImageSize({ width: img.width, height: img.height })

        // Set initial crop to center of image
        if (!initialCrop) {
          const x = (img.width - width) / 2
          const y = (img.height - height) / 2
          setCrop({ x, y, width, height })
          onCropChange({ x, y, width, height })
        }
      }

      img.addEventListener("load", handleImageLoad)
      return () => img.removeEventListener("load", handleImageLoad)
    }
  }, [imageUrl, aspectRatio, initialCrop, onCropChange])

  // Handle mouse down on crop area
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const container = containerRef.current?.getBoundingClientRect()
    if (!container) return

    const x = e.clientX - container.left
    const y = e.clientY - container.top

    // Check if clicking on resize handles
    const handleSize = 10
    const isInCorner = (cornerX: number, cornerY: number) =>
      Math.abs(x - cornerX) <= handleSize && Math.abs(y - cornerY) <= handleSize

    if (isInCorner(crop.x, crop.y)) {
      setResizing(true)
      setResizeCorner("tl")
    } else if (isInCorner(crop.x + crop.width, crop.y)) {
      setResizing(true)
      setResizeCorner("tr")
    } else if (isInCorner(crop.x, crop.y + crop.height)) {
      setResizing(true)
      setResizeCorner("bl")
    } else if (isInCorner(crop.x + crop.width, crop.y + crop.height)) {
      setResizing(true)
      setResizeCorner("br")
    } else if (x >= crop.x && x <= crop.x + crop.width && y >= crop.y && y <= crop.y + crop.height) {
      // Clicking inside crop area - start dragging
      setIsDragging(true)
      setDragStart({ x: x - crop.x, y: y - crop.y })
    }
  }

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging && !resizing) return

    const container = containerRef.current?.getBoundingClientRect()
    if (!container) return

    const x = e.clientX - container.left
    const y = e.clientY - container.top

    if (isDragging) {
      // Move the crop area
      const newX = Math.max(0, Math.min(x - dragStart.x, imageSize.width - crop.width))
      const newY = Math.max(0, Math.min(y - dragStart.y, imageSize.height - crop.height))

      const newCrop = { ...crop, x: newX, y: newY }
      setCrop(newCrop)
      onCropChange(newCrop)
    } else if (resizing) {
      // Resize the crop area
      const newCrop = { ...crop }

      if (resizeCorner === "br") {
        newCrop.width = Math.max(50, Math.min(x - crop.x, imageSize.width - crop.x))
        newCrop.height = aspectRatio
          ? newCrop.width / aspectRatio
          : Math.max(50, Math.min(y - crop.y, imageSize.height - crop.y))
      } else if (resizeCorner === "bl") {
        const newWidth = Math.max(50, Math.min(crop.x + crop.width - x, crop.x + crop.width))
        newCrop.x = crop.x + crop.width - newWidth
        newCrop.width = newWidth
        newCrop.height = aspectRatio
          ? newCrop.width / aspectRatio
          : Math.max(50, Math.min(y - crop.y, imageSize.height - crop.y))
      } else if (resizeCorner === "tr") {
        newCrop.width = Math.max(50, Math.min(x - crop.x, imageSize.width - crop.x))
        const newHeight = aspectRatio
          ? newCrop.width / aspectRatio
          : Math.max(50, Math.min(crop.y + crop.height - y, crop.y + crop.height))
        newCrop.y = crop.y + crop.height - newHeight
        newCrop.height = newHeight
      } else if (resizeCorner === "tl") {
        const newWidth = Math.max(50, Math.min(crop.x + crop.width - x, crop.x + crop.width))
        const newHeight = aspectRatio
          ? newWidth / aspectRatio
          : Math.max(50, Math.min(crop.y + crop.height - y, crop.y + crop.height))

        newCrop.x = crop.x + crop.width - newWidth
        newCrop.y = crop.y + crop.height - newHeight
        newCrop.width = newWidth
        newCrop.height = newHeight
      }

      setCrop(newCrop)
      onCropChange(newCrop)
    }
  }

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false)
    setResizing(false)
    setResizeCorner(null)
  }

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <img
        ref={imageRef}
        src={imageUrl || "/placeholder.svg"}
        alt="Crop preview"
        className="max-w-full max-h-[500px] object-contain"
      />

      <div
        className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 cursor-move"
        style={{
          left: `${crop.x}px`,
          top: `${crop.y}px`,
          width: `${crop.width}px`,
          height: `${crop.height}px`,
        }}
      >
        {/* Resize handles */}
        <div className="absolute w-3 h-3 bg-white border border-blue-500 rounded-full -top-1.5 -left-1.5 cursor-nwse-resize" />
        <div className="absolute w-3 h-3 bg-white border border-blue-500 rounded-full -top-1.5 -right-1.5 cursor-nesw-resize" />
        <div className="absolute w-3 h-3 bg-white border border-blue-500 rounded-full -bottom-1.5 -left-1.5 cursor-nesw-resize" />
        <div className="absolute w-3 h-3 bg-white border border-blue-500 rounded-full -bottom-1.5 -right-1.5 cursor-nwse-resize" />
      </div>
    </div>
  )
}

export default CropOverlay
